import React, { useState, useRef, useEffect } from 'react';
import { Phone, Mail, MapPin, MessageCircle, Image as ImageIcon, X, Plus, MapPinned, User, Upload, Loader2, Instagram, Calendar, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useLocation } from 'react-router-dom';
import { translations } from '../translations/translations';
import axios from 'axios';
import QuoteCalculator from '../components/QuoteCalculator';
import DatePicker from 'react-datepicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const t = translations[language] || translations.pt;
  const fileInputRef = useRef(null);

  // Detect region from URL
  const getRegion = () => {
    if (location.pathname.startsWith('/ie')) return 'ie';
    return 'pt';
  };
  const region = getRegion();
  const basePath = region === 'ie' ? '/ie' : '/pt';

  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    phone: '',
    email: '',
    address: '',
    eircode: '',
    service_type: '',
    preferred_date: '',
    preferred_time: '',
    message: '',
    photo_urls: []
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);

  // Time slots for Ireland
  const allTimeSlots = ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00'];

  // Helper to format date without timezone issues
  const formatLocalDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch booked slots when date changes
  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (region === 'ie' && selectedDate) {
        try {
          const dateStr = formatLocalDate(selectedDate);
          const response = await axios.get(`${API}/availability?date=${dateStr}&region=ie`);
          setBookedSlots(response.data.booked_slots || []);
        } catch (error) {
          console.error('Error fetching availability:', error);
          setBookedSlots([]);
        }
      }
    };
    fetchBookedSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, region]);

  // Check if a time slot is available
  const isTimeSlotAvailable = (time, date) => {
    if (region !== 'ie' || !date) return true;
    
    // Check if this slot is booked
    const dateStr = formatLocalDate(date);
    const isBooked = bookedSlots.some(slot => slot.date === dateStr && slot.time === time);
    if (isBooked) return false;
    
    const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    if (isWeekend) return true; // All slots available on weekends (9-21)
    
    // On weekdays, morning slots (before 14:30) are "fully booked"
    const hour = parseInt(time.split(':')[0]);
    const minute = parseInt(time.split(':')[1]);
    const timeInMinutes = hour * 60 + minute;
    
    return timeInMinutes >= 14 * 60 + 30; // Available from 14:30
  };

  // Build service options based on region
  const getServiceOptions = () => {
    if (region === 'ie') {
      // Ireland - Only cleaning services (House Cleaning focused)
      // Some services marked as "Fully Booked"
      const services = [
        { pt: 'Limpeza Regular', en: 'Regular House Cleaning', es: 'Limpieza Regular', available: true },
        { pt: 'Limpeza Profunda', en: 'Deep Cleaning', es: 'Limpieza Profunda', available: true },
        { pt: 'Limpeza de Escritório', en: 'Office Cleaning', es: 'Limpieza de Oficina', available: true },
        { pt: 'Limpeza Fim de Arrendamento', en: 'End of Tenancy Cleaning', es: 'Limpieza Fin de Alquiler', available: false },
        { pt: 'Limpeza de Estofados', en: 'Upholstery Cleaning', es: 'Limpieza de Tapicería', available: false }
      ];
      return services.map(s => ({
        label: language === 'es' ? s.es : language === 'en' ? s.en : s.pt,
        available: s.available
      }));
    } else {
      // Portugal - All services
      const services = [
        { pt: 'Pintura Externa', en: 'External Painting', es: 'Pintura Externa' },
        { pt: 'Pintura Interna', en: 'Internal Painting', es: 'Pintura Interna' },
        { pt: 'Pequenos Reparos', en: 'Small Repairs', es: 'Pequeñas Reparaciones' },
        { pt: 'Limpeza Profunda', en: 'Deep Cleaning', es: 'Limpieza Profunda' },
        { pt: 'Limpeza Doméstica', en: 'Regular Cleaning', es: 'Limpieza Doméstica' },
        { pt: 'Manutenção Geral', en: 'General Maintenance', es: 'Mantenimiento General' },
        { pt: 'Montagem de Móveis', en: 'Furniture Assembly', es: 'Montaje de Muebles' }
      ];
      return services.map(s => ({
        label: language === 'es' ? s.es : language === 'en' ? s.en : s.pt,
        available: true
      }));
    }
  };

  const serviceOptions = getServiceOptions();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceFromCalculator = (serviceName) => {
    setFormData(prev => ({
      ...prev,
      service_type: serviceName
    }));
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    if (formData.photo_urls.length + files.length > 5) {
      alert(language === 'pt' ? 'Máximo de 5 fotos permitido' : language === 'es' ? 'Máximo de 5 fotos permitido' : 'Maximum of 5 photos allowed');
      return;
    }

    setUploadingPhoto(true);
    
    try {
      const uploadedUrls = [];
      
      for (const file of Array.from(files)) {
        // Check file size on client side (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          alert(language === 'pt' ? 'Ficheiro muito grande. Máximo 10MB.' : language === 'es' ? 'Archivo muy grande. Máximo 10MB.' : 'File too large. Maximum 10MB.');
          continue;
        }
        
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        try {
          const response = await axios.post(`${API}/upload`, formDataUpload, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000 // 60 second timeout for mobile
          });
          
          uploadedUrls.push(`${BACKEND_URL}${response.data.url}`);
        } catch (uploadError) {
          console.error('Error uploading single file:', uploadError);
          const errorMsg = uploadError.response?.data?.detail || 
            (language === 'pt' ? 'Erro ao enviar uma imagem' : language === 'es' ? 'Error al subir una imagen' : 'Error uploading an image');
          alert(errorMsg);
        }
      }
      
      if (uploadedUrls.length > 0) {
        setFormData(prev => ({
          ...prev,
          photo_urls: [...prev.photo_urls, ...uploadedUrls]
        }));
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      alert(language === 'pt' ? 'Erro ao enviar imagens. Tente novamente.' : language === 'es' ? 'Error al subir imágenes. Inténtelo de nuevo.' : 'Error uploading images. Please try again.');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photo_urls: prev.photo_urls.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    // Add region and date to form data
    const submitData = {
      ...formData,
      region: region,
      preferred_date: selectedDate ? formatLocalDate(selectedDate) : ''
    };

    // Debug logging removed for production
    // console.log('Submitting form data:', submitData);
    // console.log('API URL:', `${API}/contact`);

    try {
      const response = await axios.post(`${API}/contact`, submitData);
      // console.log('Response:', response.data);
      setSubmitStatus({ type: 'success', message: t.contact.form.success });
      setFormData({
        name: '',
        gender: '',
        phone: '',
        email: '',
        address: '',
        eircode: '',
        service_type: '',
        preferred_date: '',
        preferred_time: '',
        message: '',
        photo_urls: []
      });
      setSelectedDate(null);
    } catch (error) {
      console.error('Error submitting form:', error);
      console.error('Error response:', error.response?.data);
      setSubmitStatus({ type: 'error', message: t.contact.form.error });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <section className="py-16 md:py-24 bg-gradient-to-b from-primary to-primary-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
            {t.contact.title}
          </h1>
          <p className="text-lg md:text-xl font-body mb-6">{t.contact.subtitle}</p>
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 max-w-2xl mx-auto">
            <p className="text-sm font-body">
              {language === 'pt'
                ? '📸 Pode incluir fotos do espaço/serviço pretendido para um orçamento mais preciso!'
                : '📸 You can include photos of the space/service for a more accurate quote!'}
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-green-50 border-l-4 border-accent p-4 mb-8 max-w-4xl mx-auto">
            <p className="text-sm font-body text-gray-700">
              <strong>{language === 'pt' ? 'Quando usar este formulário:' : 'When to use this form:'}</strong>
              <br />
              {language === 'pt'
                ? '✓ Quer saber o preço primeiro | ✓ Ainda não tem certeza | ✓ Precisa de mais informações | ✓ Sem compromisso'
                : '✓ Want to know the price first | ✓ Not sure yet | ✓ Need more information | ✓ No commitment'}
            </p>
          </div>

          {/* Pricing Reference Section - Only for Ireland */}
          {region === 'ie' && (
            <div className="mb-12 bg-gradient-to-r from-[#1a5c3a]/5 to-[#d4a84b]/5 rounded-2xl p-8 border border-[#1a5c3a]/10">
              <h3 className="text-2xl font-heading font-bold text-gray-900 mb-6 text-center">
                {language === 'pt' ? 'Referência de Preços' : 'Price Reference'}
              </h3>
              
              {/* Main Services */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <div className="text-2xl mb-2">🏠</div>
                  <h4 className="font-heading font-bold text-gray-900 mb-2">
                    {language === 'pt' ? 'Limpeza Regular' : 'Regular House Cleaning'}
                  </h4>
                  <p className="text-[#1a5c3a] font-bold">
                    {language === 'pt' ? 'Desde' : 'From'} €25/hr <span className="text-gray-500 font-normal text-sm">(1 cleaner)</span>
                  </p>
                  <p className="text-[#1a5c3a] font-bold">
                    {language === 'pt' ? 'Desde' : 'From'} €50/hr <span className="text-gray-500 font-normal text-sm">(2 cleaners)</span>
                  </p>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border-2 border-[#d4a84b]">
                  <div className="text-2xl mb-2">✨</div>
                  <h4 className="font-heading font-bold text-gray-900 mb-2">
                    {language === 'pt' ? 'Limpeza Profunda' : 'Deep Cleaning'}
                  </h4>
                  <p className="text-[#1a5c3a] font-bold">
                    {language === 'pt' ? 'Desde' : 'From'} €30/hr <span className="text-gray-500 font-normal text-sm">(1 cleaner)</span>
                  </p>
                  <span className="inline-block mt-2 px-2 py-1 bg-[#d4a84b] text-white text-xs rounded-full">
                    {language === 'pt' ? 'Mais Popular' : 'Most Popular'}
                  </span>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <div className="text-2xl mb-2">🚚</div>
                  <h4 className="font-heading font-bold text-gray-900 mb-2">
                    {language === 'pt' ? 'Mudança' : 'Move-In / Move-Out'}
                  </h4>
                  <p className="text-[#d4a84b] font-bold">
                    {language === 'pt' ? 'Orçamento Personalizado' : 'Custom Quote'}
                  </p>
                </div>
              </div>

              {/* Extra Services */}
              <div className="mb-6">
                <h4 className="font-heading font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span>🧼</span> {language === 'pt' ? 'Serviços Extra' : 'Extra Services'}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-600">{language === 'pt' ? 'Forno' : 'Oven'}</p>
                    <p className="font-bold text-[#1a5c3a]">€40+</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-600">{language === 'pt' ? 'Frigorífico' : 'Fridge'}</p>
                    <p className="font-bold text-[#1a5c3a]">€25+</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-600">{language === 'pt' ? 'Janelas' : 'Windows'}</p>
                    <p className="font-bold text-[#1a5c3a]">€30+</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-600">{language === 'pt' ? 'Engomar' : 'Ironing'}</p>
                    <p className="font-bold text-[#1a5c3a]">€20/hr</p>
                  </div>
                </div>
              </div>

              {/* Booking Info */}
              <div className="bg-white rounded-xl p-4 mb-4">
                <div className="flex flex-wrap gap-4 justify-center text-sm">
                  <span className="flex items-center gap-2 text-gray-700">
                    <span className="text-[#1a5c3a]">✓</span> {language === 'pt' ? 'Mínimo: 2 horas' : 'Minimum: 2 hours'}
                  </span>
                  <span className="flex items-center gap-2 text-gray-700">
                    <span className="text-[#1a5c3a]">✓</span> {language === 'pt' ? '1 ou 2 profissionais' : '1 or 2 cleaners'}
                  </span>
                  <span className="flex items-center gap-2 text-gray-700">
                    <span className="text-[#1a5c3a]">✓</span> {language === 'pt' ? 'Serviço profissional' : 'Professional service'}
                  </span>
                </div>
              </div>

              {/* Important Note */}
              <div className="bg-[#d4a84b]/10 rounded-lg p-4 border border-[#d4a84b]/30">
                <p className="text-sm text-gray-700 text-center">
                  <strong>{language === 'pt' ? 'Nota:' : 'Note:'}</strong>{' '}
                  {language === 'pt' 
                    ? 'Os preços apresentados são valores iniciais. O orçamento final será confirmado após análise de fotos ou avaliação no local.'
                    : 'Prices shown are starting prices. Final quote confirmed after reviewing photos or on-site assessment.'}
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-heading font-semibold text-gray-900 mb-6">
                {t.contact.form.title}
              </h2>
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-body font-medium text-gray-700 mb-2">
                    {t.contact.form.name}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    data-testid="input-name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-gray-700 mb-2">
                    {language === 'pt' ? 'Género' : 'Gender'}
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="gender"
                        value="male"
                        checked={formData.gender === 'male'}
                        onChange={handleChange}
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                        data-testid="gender-male"
                      />
                      <span className="text-sm font-body text-gray-700">{language === 'pt' ? 'Masculino' : 'Male'}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="gender"
                        value="female"
                        checked={formData.gender === 'female'}
                        onChange={handleChange}
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                        data-testid="gender-female"
                      />
                      <span className="text-sm font-body text-gray-700">{language === 'pt' ? 'Feminino' : 'Female'}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="gender"
                        value="other"
                        checked={formData.gender === 'other'}
                        onChange={handleChange}
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                        data-testid="gender-other"
                      />
                      <span className="text-sm font-body text-gray-700">{language === 'pt' ? 'Outro' : 'Other'}</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-body font-medium text-gray-700 mb-2">
                    {t.contact.form.phone}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    data-testid="input-phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-body font-medium text-gray-700 mb-2">
                    {t.contact.form.email}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    data-testid="input-email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
                  />
                </div>

                {/* Quote Calculator */}
                <div className="md:col-span-2">
                  <QuoteCalculator 
                    language={language} 
                    onServiceSelect={handleServiceFromCalculator}
                    region={region}
                  />
                </div>

                <div>
                  <label htmlFor="service_type" className="block text-sm font-body font-medium text-gray-700 mb-2">
                    {t.contact.form.service}
                  </label>
                  <select
                    id="service_type"
                    name="service_type"
                    data-testid="select-service"
                    value={formData.service_type}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
                  >
                    <option value="">{t.contact.form.servicePlaceholder}</option>
                    {serviceOptions.map((service, index) => (
                      <option 
                        key={index} 
                        value={service.available ? service.label : ''} 
                        disabled={!service.available}
                        className={!service.available ? 'text-gray-400' : ''}
                      >
                        {service.label}{!service.available ? ` (${language === 'pt' ? 'Agenda Cheia' : 'Fully Booked'})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Deep Cleaning Info Box - Ireland only */}
                {region === 'ie' && formData.service_type && formData.service_type.toLowerCase().includes('deep') && (
                  <div className="md:col-span-2 p-4 bg-[#1a5c3a]/10 border border-[#1a5c3a]/20 rounded-lg">
                    <h4 className="font-semibold text-[#1a5c3a] mb-2 flex items-center gap-2">
                      <span>🧼</span> Deep Cleaning in Dublin
                    </h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• <strong>2 bedroom house (full property):</strong> from €250 (depending on condition)</li>
                      <li>• <strong>Includes:</strong> bedrooms, bathroom, kitchen & common areas</li>
                      <li>• <strong>3+ bedrooms:</strong> price adjusted based on size and cleaning level</li>
                      <li>• <strong>Quote available:</strong> via photos or on-site assessment before service</li>
                    </ul>
                  </div>
                )}

                <div>
                  <label htmlFor="address" className="block text-sm font-body font-medium text-gray-700 mb-2">
                    {language === 'pt' ? 'Morada do Serviço' : 'Service Address'}
                  </label>
                  <div className="relative">
                    <MapPinned size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      id="address"
                      name="address"
                      data-testid="input-address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder={language === 'pt' ? 'Rua, número, cidade...' : 'Street, number, city...'}
                      className="w-full h-12 pl-11 pr-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
                    />
                  </div>
                </div>

                {/* Eircode field - Ireland only */}
                {region === 'ie' && (
                  <div>
                    <label htmlFor="eircode" className="block text-sm font-body font-medium text-gray-700 mb-2">
                      Eircode
                      <span className="text-gray-400 font-normal ml-2">(e.g. D02 X285)</span>
                    </label>
                    <div className="relative">
                      <MapPinned size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        id="eircode"
                        name="eircode"
                        data-testid="input-eircode"
                        value={formData.eircode}
                        onChange={handleChange}
                        placeholder="A65 F4E2"
                        maxLength={8}
                        className="w-full h-12 pl-11 pr-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body uppercase"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {"Don't know your Eircode? "}<a href="https://finder.eircode.ie" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Find it here</a>
                    </p>
                  </div>
                )}

                {/* Preferred Date and Time - Ireland only */}
                {region === 'ie' && (
                  <>
                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-body font-medium text-gray-700 mb-2">
                          <Calendar size={18} className="inline mr-2" />
                          Preferred Date
                        </label>
                        <DatePicker
                          selected={selectedDate}
                          onChange={(date) => setSelectedDate(date)}
                          minDate={new Date()}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="Choose date"
                          className="w-full h-12 px-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
                          data-testid="input-preferred-date"
                        />
                      </div>

                      <div>
                        <label htmlFor="preferred_time" className="block text-sm font-body font-medium text-gray-700 mb-2">
                          <Clock size={18} className="inline mr-2" />
                          Preferred Time
                        </label>
                        <select
                          id="preferred_time"
                          name="preferred_time"
                          data-testid="input-preferred-time"
                          value={formData.preferred_time}
                          onChange={handleChange}
                          className="w-full h-12 px-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
                        >
                          <option value="">Select time</option>
                          {allTimeSlots.map((time, index) => {
                            const available = isTimeSlotAvailable(time, selectedDate);
                            return (
                              <option 
                                key={index} 
                                value={available ? time : ''} 
                                disabled={!available}
                              >
                                {time}{!available ? ' (Fully Booked)' : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>

                    {/* Preference Notice */}
                    <div className="md:col-span-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <span className="font-medium">⚠️ Note:</span>{' '}
                        The selected date and time are a preference only. Final confirmation will be provided after your quote is approved and availability is confirmed.
                      </p>
                    </div>
                  </>
                )}

                <div>
                  <label htmlFor="message" className="block text-sm font-body font-medium text-gray-700 mb-2">
                    {t.contact.form.message}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="input-message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="4"
                    placeholder={language === 'pt' ? 'Descreva o serviço que precisa, incluindo detalhes como tamanho do espaço, materiais necessários, etc.' : 'Describe the service you need, including details like space size, materials needed, etc.'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent font-body"
                  ></textarea>
                </div>

                {/* Photo Upload Section */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <label className="block text-sm font-body font-medium text-gray-700 mb-3">
                    <ImageIcon size={18} className="inline mr-2" />
                    {language === 'pt' ? 'Fotos do Espaço/Serviço (Opcional)' : 'Photos of Space/Service (Optional)'}
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    {language === 'pt' 
                      ? 'Envie fotos do espaço para ajudar na avaliação (máx. 5 fotos, 5MB cada)' 
                      : 'Upload photos of the space to help with evaluation (max. 5 photos, 5MB each)'}
                  </p>
                  
                  {/* Upload Button */}
                  <div className="mb-4">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="photo-file-input"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto || formData.photo_urls.length >= 5}
                      className="w-full h-24 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center gap-2"
                      data-testid="upload-photo-button"
                    >
                      {uploadingPhoto ? (
                        <>
                          <Loader2 size={24} className="text-primary animate-spin" />
                          <span className="text-sm text-gray-600">{language === 'pt' ? 'Enviando...' : 'Uploading...'}</span>
                        </>
                      ) : (
                        <>
                          <Upload size={24} className="text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {language === 'pt' ? 'Clique para selecionar fotos' : 'Click to select photos'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {language === 'pt' ? `${5 - formData.photo_urls.length} fotos restantes` : `${5 - formData.photo_urls.length} photos remaining`}
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                  
                  {/* Photo Preview Grid */}
                  {formData.photo_urls.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {formData.photo_urls.map((url, index) => (
                        <div key={index} className="relative group">
                          <img 
                            src={url} 
                            alt={`${language === 'pt' ? 'Foto' : 'Photo'} ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border border-gray-200"
                            onError={(e) => e.target.src = 'https://via.placeholder.com/100x80?text=Erro'}
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                            data-testid={`remove-photo-${index}`}
                          >
                            <X size={14} />
                          </button>
                          <div className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
                            {index + 1}/{formData.photo_urls.length}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {submitStatus.message && (
                  <div
                    data-testid="form-status-message"
                    className={`p-4 rounded-md ${
                      submitStatus.type === 'success'
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}
                  >
                    {submitStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="submit-button"
                  disabled={isSubmitting}
                  className="w-full h-12 bg-primary text-white font-body font-semibold rounded-md hover:bg-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (language === 'pt' ? 'Enviando...' : 'Sending...') : t.contact.form.submit}
                </button>
              </form>
            </div>

            <div>
              <h2 className="text-3xl font-heading font-semibold text-gray-900 mb-6">
                {t.contact.info.title}
              </h2>
              <div className="space-y-6">
                {/* WhatsApp - Primary Contact */}
                <div className="flex items-start" data-testid="contact-whatsapp">
                  <MessageCircle size={24} className="text-[#25D366] mr-4 mt-1" />
                  <div>
                    <h3 className="font-body font-semibold text-gray-900 mb-1">{t.contact.info.whatsapp}</h3>
                    <a
                      href={region === 'ie' ? 'https://wa.me/353899502499' : 'https://wa.me/353899502499'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-[#25D366] font-body"
                    >
                      +353 89 950 2499
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start" data-testid="contact-email">
                  <Mail size={24} className="text-primary mr-4 mt-1" />
                  <div>
                    <h3 className="font-body font-semibold text-gray-900 mb-1">{t.contact.info.email}</h3>
                    <a
                      href={region === 'ie' ? 'mailto:suporte@sancurservices.com' : 'mailto:support@sancurservices.com'}
                      className="text-gray-600 hover:text-primary font-body break-all"
                    >
                      {region === 'ie' ? 'suporte@sancurservices.com' : 'support@sancurservices.com'}
                    </a>
                  </div>
                </div>

                {/* Instagram - Both regions */}
                <div className="flex items-start" data-testid="contact-instagram">
                  <Instagram size={24} className="text-primary mr-4 mt-1" />
                  <div>
                    <h3 className="font-body font-semibold text-gray-900 mb-1">Instagram</h3>
                    <a
                      href="https://instagram.com/sancurservices"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-primary font-body"
                    >
                      @sancurservices
                    </a>
                  </div>
                </div>

                {/* Area - Different per region */}
                <div className="flex items-start" data-testid="contact-area">
                  <MapPin size={24} className="text-primary mr-4 mt-1" />
                  <div>
                    <h3 className="font-body font-semibold text-gray-900 mb-1">{t.contact.info.area}</h3>
                    <p className="text-gray-600 font-body">
                      {region === 'ie' 
                        ? (language === 'pt' ? 'Toda região de Dublin' : 'All Dublin region')
                        : t.contact.info.areaValue
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-gray-50 rounded-xl">
                <h3 className="font-heading font-semibold text-gray-900 mb-3">
                  {language === 'pt' ? 'Horário de Atendimento' : 'Business Hours'}
                </h3>
                {region === 'ie' ? (
                  <>
                    <p className="text-gray-700 font-body">
                      {language === 'pt' ? 'Segunda a Domingo: 9h - 21h' : 'Monday to Sunday: 9am - 9pm'}
                    </p>
                    <p className="text-gray-500 text-sm font-body mt-1">
                      {language === 'pt' ? '* Horários sujeitos a disponibilidade' : '* Hours subject to availability'}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-gray-700 font-body">
                      {language === 'pt' ? 'Segunda a Sábado: 8h - 18h' : 'Monday to Saturday: 8am - 6pm'}
                    </p>
                    <p className="text-gray-700 font-body">
                      {language === 'pt' ? 'Domingo: Fechado' : 'Sunday: Closed'}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
